import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { useKoko, ApiStatus } from 'shared/api/koko'
import { KokoLogo, KokoGuy } from './icons'
import { giphyPurple, giphyIndigo, giphyRed, giphyPink, giphyWhite, giphyDarkestGrey } from '@giphy/js-brand'
import { googleAnalyticsEvent } from 'shared/util/analytics'

const Container = styled.div<{ isAlarming: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 120px;
    margin-bottom: 50px;
    border-radius: 8px;

    ${(props) =>
        props.isAlarming
            ? css`
                  background: linear-gradient(-135deg, ${giphyRed} 0%, ${giphyPink} 100%);
              `
            : css`
                  background: linear-gradient(-135deg, ${giphyPurple} 0%, ${giphyIndigo} 100%);
              `}
`

const Title = styled.h3`
    font-family: NexaBlack;
    font-size: 26px;
    color: ${giphyWhite};
    margin-bottom: 0;
    margin-top: 0;
`

const Description = styled.p`
    margin-top: 3px;
    margin-bottom: 0;
    font-size: 16px;
    color: ${giphyWhite};
`

const Left = styled.div`
    flex: 1;
    display: flex;
`

const LeftImage = styled.div`
    height: 120px;
    width: 133px;
    position: relative;

    svg {
        position: absolute;
        bottom: 0;
        transform: scale(-1, 1);
        height: 95px;
        fill: white;
    }
`

const LeftTexts = styled.div`
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    justify-content: center;
    margin-left: 18px;
`

const Right = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-right: 50px;
`

const PartnershipText = styled.div`
    margin-bottom: 7px;
    font-size: 15px;
    font-weight: bold;
    opacity: 0.5;
`

const GetHelpButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    width: 251px;
    font-size: 17px;
    background: ${giphyDarkestGrey};
    border-radius: 2px;
`

const PartnershipContainer = styled.div`
    display: flex;

    svg {
        height: 20px;
        fill: white;
        margin-left: 8px;
    }
`

const CloseMessage = styled.i`
    cursor: pointer;
    position: absolute;
    right: 10px;
    top: 10px;
    color: ${giphyWhite};
`

type KokoProps = {
    term: string
}

const Koko = ({ term }: KokoProps) => {
    const { status, data } = useKoko({ term })
    const [isClosed, setIsClosed] = useState<boolean>(false)

    const handleGetHelp = () => {
        window.open(`https://r.kokocares.org/giphy?q=${term}`, '_blank')
    }

    useEffect(() => {
        if (data?.match_koko_keywords_list) {
            googleAnalyticsEvent({
                category: 'koko',
                action: 'banner_open',
                label: term,
            })
        }
    }, [data?.match_koko_keywords_list])

    if (ApiStatus.IDLE === status || status === ApiStatus.FETCHING || isClosed) {
        return null
    }

    if (ApiStatus.FETCHED && data?.match_koko_keywords_list) {
        return (
            <Container isAlarming={!!data.is_alarming}>
                <Left>
                    <LeftImage>
                        <KokoGuy />
                    </LeftImage>
                    <LeftTexts>
                        <Title>Is everything ok? {!data.is_alarming && '❤️'}</Title>
                        <Description>
                            If you or someone you know is struggling, you are not alone. Help is available.
                        </Description>
                    </LeftTexts>
                </Left>
                <Right>
                    <PartnershipContainer>
                        <PartnershipText>Partnership with</PartnershipText>
                        <KokoLogo />
                    </PartnershipContainer>
                    <GetHelpButton onClick={handleGetHelp}>Get Help</GetHelpButton>
                </Right>
                <CloseMessage className="ss ss-icon ss-delete" onClick={() => setIsClosed(true)} />
            </Container>
        )
    }

    return null
}

export default Koko
